import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageShell } from '@noor/ui';
import {
  MODERATION_ENTITY_TYPES,
  listModerationProductCategories,
  type ChangeRequest,
  type ModerationListSearch,
} from '@/entities/moderation';
import { ModerationTable } from '@/widgets/moderation-table';
import { ModerationDetailModal } from '../ui/moderation-detail-modal';
import { ModerationFilters, ModerationQueueHeader } from '../ui/moderation-filters';

interface ProductsModerationPageProps {
  /** Resolved list params from the route's validated search schema. */
  params: ModerationListSearch;
  /** Patch the URL search params (the source of truth for queue state). */
  onParamsChange: (next: Partial<ModerationListSearch>) => void;
}

/**
 * Products moderation queue. Deliberately standalone — every queue owns
 * its composition and shares only the building-block components.
 */
export function ProductsModerationPage({
  params,
  onParamsChange,
}: ProductsModerationPageProps) {
  const [selected, setSelected] = useState<ChangeRequest | null>(null);
  const categories = useQuery({
    queryKey: ['moderation-product-categories'],
    queryFn: listModerationProductCategories,
  });

  return (
    <PageShell variant="fill" className="gap-5 p-0 lg:p-0">
      <div className="shrink-0">
        <ModerationQueueHeader
          params={params}
          onParamsChange={onParamsChange}
          titleKey={MODERATION_ENTITY_TYPES.products.title}
          toolbar={<ModerationFilters params={params} onParamsChange={onParamsChange} />}
          productCategories={categories.data ?? []}
          productCategoryId={params.productCategoryId}
        />
      </div>
      <ModerationTable
        entityType="products"
        params={params}
        onParamsChange={onParamsChange}
        onSelect={setSelected}
      />
      <ModerationDetailModal
        entityType="products"
        request={selected}
        onClose={() => setSelected(null)}
      />
    </PageShell>
  );
}
